import { ChangeDetectionStrategy, Component, inject, Input, OnInit, output, ViewEncapsulation } from '@angular/core';
import {
    ITask2ReportAvailableItems,
    ITask2ReportItem,
    ITask2ReportParameter,
    ITask2ReportSummary,
    ITask2ReportWidget,
    ITask2ReportWidgetFilter,
} from '../../fi-task-v2-report';
import { FiRemoteStateFullDataSource } from '@fi-app/shared/fi-items-list/fi-remote-state-full-data-source';
import { FiItemsListType } from '@fi-app/shared/fi-items-list/fi-items-list-type';
import { ObjectTypes } from '@fi-app/shared/types';
import { FiTaskReportType } from '../../fi-task-v2-report-enums';
import { Sort } from '@angular/material/sort';
import { MatSort } from '@angular/material/sort';
import { ILoadDataArgs } from '@fi-app/shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '@fi-app/shared/fi-items-list/fi-items-list';
import { FiTaskV2ReportService } from '../../services/fi-task-v2-report.service';
import { BehaviorSubject, takeUntil, debounceTime, Observable } from 'rxjs';
import { FiTaskV2ReportHelperService } from '../../services/fi-task-v2-report-helper.service';
import { FiTask2ReportDateService } from '../../services/fi-task-v2-report-date.service';

@Component({
    selector: 'fi-widget-details-content',
    templateUrl: './widget-details-content.component.html',
    styleUrls: ['./widget-details-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-widget-details-content' },
    standalone: false,
})
export class WidgetDetailsContentComponent extends FiRemoteStateFullDataSource<ITask2ReportItem> {
    @Input({ required: true }) widget: ITask2ReportWidget;
    @Input({ required: true }) currentFilter: ITask2ReportWidgetFilter;

    @Input({ required: true }) selectedFilterItemsChanged$: Observable<void>;

    public addNewAvailableItems = output<ITask2ReportAvailableItems>();
    // numberOfRecordsToTake = 100000; // for tests, remove later
    protected summary$ = new BehaviorSubject<ITask2ReportSummary>(undefined);

    get defaultSortColumnName(): string {
        return this.widget.ReportType !== FiTaskReportType.Overdue ? 'ReportStatus' : 'Deadline';
    }
    get defaultSortDirection(): boolean {
        return false;
    }
    protected skipInitSelectedSort = true; // always use defaultSortColumnName & defaultSortDirection

    public itemsListType = FiItemsListType.TaskV2ReportWidget;
    public objectType = ObjectTypes.Task2Reports;
    protected chartConfig: {
        isOverdueOnly: boolean;
        chartHeading: string;
    };

    private fiTaskV2ReportHelperService = inject(FiTaskV2ReportHelperService);
    private fiTaskV2ReportService = inject(FiTaskV2ReportService);
    private fiTask2ReportDateService = inject(FiTask2ReportDateService);

    public ngOnInit(): void {
        this.setChartConfig();
        this.subscribeOnFilterItemsChange();
        this.subscribeOnDateRangeChanged();
    }

    protected setChartConfig(): void {
        this.chartConfig = {
            isOverdueOnly: this.fiTaskV2ReportHelperService.isOverdueWidget(this.widget),
            chartHeading: this.fiTaskV2ReportHelperService.getTaskReportDescription(this.widget),
        };
    }

    public onSortChanged($event: Sort): void {
        this.matSort = { active: $event.active, direction: $event.direction } as MatSort;
        super.onSortChanged($event);
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<ITask2ReportItem>> {
        const parameter = this.getReportParameter(loadArgs);
        const promise = new Promise<IItemsListDataFetchResult<ITask2ReportItem>>((resolve, reject) => {
            this.fiTaskV2ReportService.getReportData(parameter).then((reportData) => {
                resolve({
                    Items: reportData.Tasks,
                    TotalCount: undefined,
                    LightWeightObjectsForLocalSearch: undefined,
                });

                if (parameter.LoadSummary) {
                    this.summary$.next(reportData.Summary);
                }
                if (parameter.LoadAvailableItems) {
                    this.addNewAvailableItems.emit(reportData.AvailableItems);
                }
            }, reject);
        });
        return promise;
    }

    protected onTaskStatusUpdated(): void {
        const parameter = this.getReportParameter({});
        this.fiTaskV2ReportService.getReportSummary(parameter).then((summary) => {
            this.summary$.next(summary);
        });
    }

    private loadAvailableItems = true;
    private getReportParameter(loadArgs: ILoadDataArgs): ITask2ReportParameter {
        const parameter = super.getItemsListParameter(loadArgs) as ITask2ReportParameter;

        parameter.ReportType = this.widget.ReportType;
        parameter.Filter = this.currentFilter;
        parameter.DateRange = this.fiTask2ReportDateService.getDateRange(this.widget.Id);
        parameter.LoadSummary = !loadArgs.fetchOnlyItems;
        parameter.LoadAvailableItems = this.loadAvailableItems;
        this.loadAvailableItems = false;

        return parameter;
    }

    protected isOverdueReport(): boolean {
        return this.widget.ReportType === FiTaskReportType.Overdue;
    }

    private subscribeOnFilterItemsChange(): void {
        this.selectedFilterItemsChanged$.pipe(debounceTime(500), takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.reloadData();
        });
    }

    private subscribeOnDateRangeChanged(): void {
        this.fiTask2ReportDateService.subscribeOnDateRangeChanged(this.widget.Id, this.ngOnDestroy$, () => {
            this.reloadData();
        });
    }

    protected setSortData() {
        super.setSortData();
        if (!this.isOverdueReport()) {
            return;
        }

        this.sortColumn = this.defaultSortColumnName;
    }
}
